const memberUrlFields = {
  companyWebsite: { label: "Member Website", example: "https://www.yourcompany.com" },
  googleReviewUrl: { label: "Member Google Review URL", example: "https://g.page/r/..." },
  facebookUrl: { label: "Facebook Business Page URL", example: "https://www.facebook.com/yourpage" },
  instagramUrl: { label: "Instagram Account URL", example: "https://www.instagram.com/youraccount" },
  threadsUrl: { label: "Threads Account URL", example: "https://www.threads.net/@youraccount" },
  linkedinUrl: { label: "LinkedIn Page URL", example: "https://www.linkedin.com/company/yourcompany" },
  xTwitterUrl: { label: "X (Twitter) Account URL", example: "https://x.com/youraccount" },
  tikTokUrl: { label: "TikTok Account URL", example: "https://www.tiktok.com/@youraccount" },
  youtubeUrl: { label: "Youtube Channel URL", example: "https://www.youtube.com/@yourchannel" },
} as const;

type MemberUrlField = keyof typeof memberUrlFields;
type MemberUrlValues = Partial<Record<MemberUrlField, string | null | undefined>>;

// A public domain name: dot-separated labels and a letter (or punycode) top
// level, such as "www.example.com". Handles ("@account"), names with spaces
// and single words are not web addresses.
const memberUrlHostnamePattern =
  /^(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.)+(?:[a-z]{2,63}|xn--[a-z\d-]{1,59})$/;

/**
 * Same rule as `isCmpMemberUrlValid` in cap-backend
 * src/utilities/cmpMemberApplications.ts; keep the two in step. The hostname
 * is checked explicitly because the browser URL parser accepts hostnames the
 * server refuses (Chromium reads "Ebony Child" as "https://ebony%20child/").
 */
export const isValidMemberUrl = (value?: string | null): boolean => {
  const trimmedValue = (value || "").trim();
  if (!trimmedValue) return true;
  // A copied web address never contains spaces.
  if (/\s/.test(trimmedValue)) return false;
  const urlValue = trimmedValue.startsWith("//")
    ? `https:${trimmedValue}`
    : /^[a-z][a-z\d+.-]*:/i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;

  try {
    const parsed = new URL(urlValue);
    return (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      parsed.hostname.length <= 253 &&
      memberUrlHostnamePattern.test(parsed.hostname) &&
      !parsed.username &&
      !parsed.password
    );
  } catch {
    return false;
  }
};

/** Tells staff what to enter. The error shows under the field it names. */
export const getMemberUrlFieldErrorMessage = (field: MemberUrlField): string => {
  const { label, example } = memberUrlFields[field];
  return `${label} is not a full web address. Enter the full address, for example ${example}${example.endsWith(".") ? "" : "."} Leave it empty if the member does not have one.`;
};

/** A saved baseline exempts unchanged legacy links from unrelated card edits. */
export const getMemberUrlFieldErrors = (
  values: MemberUrlValues,
  savedValues?: MemberUrlValues,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  (Object.keys(memberUrlFields) as MemberUrlField[]).forEach((field) => {
    const value = (values[field] || "").trim();
    if (savedValues && value === (savedValues[field] || "").trim()) return;
    if (!isValidMemberUrl(value)) {
      errors[field] = getMemberUrlFieldErrorMessage(field);
    }
  });
  return errors;
};
